/**
 * The event's guests, for the producer (ADR-JSS-0012, participant-loop
 * contracts v2 §7).
 *
 * The site declares N warm slots; the producer decides who is on each. Take
 * routes a selected admitted participant onto slot k (replacing any occupant,
 * who stays admitted), Clear empties a slot, and Fill all runs the conference
 * fill. A take is a route change at the participant gateway: nothing here
 * calls JBS, and an empty slot stays at the site, black and silent.
 *
 * Every admitted participant holds a session to the gateway whether or not
 * they are on a slot, so the panel shows their presence. A disconnected one
 * keeps their place, and their slot, until `presenceTimeoutS` runs out; the
 * panel counts that down. event-manager does the removing, not this page.
 *
 * The slot routes, the read included, and Approve, Reject and Remove are
 * the moderator's (PARTICIPANT_MODERATOR_ROLE, the Event Operator persona):
 * the BFF and event-manager refuse them to anyone else. So the panel never
 * calls them for anyone else either. A Viewer gets a read-only roster built
 * from the participant list every org member may read: who is on which slot,
 * who is waiting, and how many have asked to join. It has no controls and no
 * presence.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mic, MicOff, Users, UserCheck, UserX, Video, VideoOff } from 'lucide-react';
import { canActAs, PARTICIPANT_MODERATOR_ROLE } from '@jeket-cloud/types/auth';
import { participantSlotsApi, participantsApi } from '@/api/client';
import { useUserRoles } from '@/contexts/AuthContext';
import { parseApiError } from '@/lib/apiError';
import { cn } from '@/lib/utils';
import type { EventParticipant, ParticipantSlot, ParticipantSlotsView, SlotParticipant } from '@/types';

/** How often the slots and presence are re-read. A take is human-paced; the countdown ticks locally. */
const POLL_MS = 2_000;

type SlotAction =
  | { kind: 'take'; slot: number; participant: SlotParticipant }
  | { kind: 'clear'; slot: number }
  | { kind: 'fill' }
  | { kind: 'approve' | 'reject' | 'remove'; participant: SlotParticipant };

function describe(action: SlotAction): string {
  switch (action.kind) {
    case 'take':
      return `Taking ${action.participant.displayName} onto slot ${action.slot}`;
    case 'clear':
      return `Clearing slot ${action.slot}`;
    case 'fill':
      return 'Fill all';
    case 'approve':
      return `Approving ${action.participant.displayName}`;
    case 'reject':
      return `Rejecting ${action.participant.displayName}`;
    case 'remove':
      return `Removing ${action.participant.displayName}`;
  }
}

function perform(eventId: string, action: SlotAction): Promise<unknown> {
  switch (action.kind) {
    case 'take':
      return participantSlotsApi.take(eventId, action.slot, action.participant.participantId);
    case 'clear':
      return participantSlotsApi.clear(eventId, action.slot);
    case 'fill':
      return participantSlotsApi.fill(eventId);
    case 'approve':
      return participantsApi.approve(eventId, action.participant.participantId);
    case 'reject':
      return participantsApi.reject(eventId, action.participant.participantId);
    case 'remove':
      return participantsApi.remove(eventId, action.participant.participantId);
  }
}

/**
 * One line saying which action failed and why. The slot routes answer 409
 * `{ error, slotCount }` for a slot beyond N, and a bare 409 for a
 * participant who is not admitted (any more).
 */
function refusalMessage(action: SlotAction, err: unknown): string {
  const parsed = parseApiError(err);
  const what = `${describe(action)} was refused`;
  if (parsed.status === 409 && (action.kind === 'take' || action.kind === 'clear')) {
    const slotCount = (parsed.body as { slotCount?: unknown } | null)?.slotCount;
    if (typeof slotCount === 'number') {
      return `${what}: the site has declared ${slotCount} slot${slotCount === 1 ? '' : 's'}, so there is no slot ${action.slot}.`;
    }
    if (action.kind === 'take') {
      return `${what}: ${action.participant.displayName} is not admitted (${parsed.message}).`;
    }
  }
  return `${what}: ${parsed.message}`;
}

/** A slot write's answer: the whole view, as GET returns it. */
function isSlotsView(v: unknown): v is ParticipantSlotsView {
  return !!v && typeof v === 'object' && Array.isArray((v as ParticipantSlotsView).slots);
}

/** Wall-clock ms, re-read every `ms` while `active`. */
function useNow(ms: number, active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms, active]);
  return now;
}

/**
 * Seconds left before event-manager removes a disconnected participant. The
 * clock is the browser's and `disconnectedAt` the server's, so the value is
 * clamped to the timeout: skew can shorten the countdown, never stretch it.
 */
export function presenceSecondsLeft(disconnectedAt: string | null, timeoutS: number, nowMs: number): number | null {
  const since = disconnectedAt ? Date.parse(disconnectedAt) : NaN;
  if (!Number.isFinite(since)) return null;
  return Math.min(timeoutS, Math.max(0, Math.ceil(timeoutS - (nowMs - since) / 1000)));
}

function Presence({ p, timeoutS, now }: { p: SlotParticipant; timeoutS: number; now: number }) {
  if (p.connected) {
    return (
      <span className="inline-flex items-center gap-1 text-jeket-healthy" data-testid="presence">
        <span className="w-1.5 h-1.5 rounded-full bg-jeket-healthy" aria-hidden="true" /> Connected
      </span>
    );
  }
  const left = presenceSecondsLeft(p.disconnectedAt, timeoutS, now);
  return (
    <span className="inline-flex items-center gap-1 text-orange-400" data-testid="presence">
      <span className="w-1.5 h-1.5 rounded-full bg-orange-400" aria-hidden="true" />
      {left === null ? 'Disconnected' : left > 0 ? `Disconnected · removed in ${left} s` : 'Disconnected · removing'}
    </span>
  );
}

function MediaState({ p }: { p: SlotParticipant }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-jeket-silver-muted">
      {p.cameraEnabled ? <Video size={11} aria-label="camera on" /> : <VideoOff size={11} aria-label="camera off" />}
      {p.micEnabled ? <Mic size={11} aria-label="mic on" /> : <MicOff size={11} aria-label="mic off" />}
    </span>
  );
}

const actionButton =
  'inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

function SlotCard({
  slot,
  occupant,
  selected,
  timeoutS,
  now,
  busy,
  onAction,
}: {
  slot: ParticipantSlot;
  occupant: SlotParticipant | null;
  selected: SlotParticipant | null;
  timeoutS: number;
  now: number;
  busy: boolean;
  onAction: (a: SlotAction) => void;
}) {
  const empty = slot.participantId === null;
  const takeTarget = selected && selected.participantId !== slot.participantId ? selected : null;
  return (
    <div
      role="group"
      aria-label={`Slot ${slot.slot}`}
      data-testid={`slot-${slot.slot}`}
      className={cn(
        'bg-jeket-black-surface border rounded-lg p-4 space-y-3',
        empty ? 'border-jeket-black-elevated' : 'border-red-500/40',
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-jeket-silver-muted">Slot {slot.slot}</span>
        <span
          className={cn(
            'px-2 py-0.5 rounded-full text-[10px] font-medium',
            empty ? 'bg-gray-500/20 text-gray-400' : 'bg-red-500/20 text-red-400',
          )}
        >
          {empty ? 'Empty' : 'On air'}
        </span>
      </div>
      {empty ? (
        <p className="text-sm text-jeket-silver-muted">Empty: black and silent at the site.</p>
      ) : (
        <div className="space-y-1">
          <p className="text-sm font-medium text-jeket-silver-bright" data-testid={`slot-${slot.slot}-occupant`}>
            {occupant?.displayName ?? 'Unknown participant'}
          </p>
          {occupant && (
            <div className="flex items-center gap-3 text-[11px]">
              <Presence p={occupant} timeoutS={timeoutS} now={now} />
              <MediaState p={occupant} />
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-2">
        <button
          onClick={() => takeTarget && onAction({ kind: 'take', slot: slot.slot, participant: takeTarget })}
          disabled={busy || !takeTarget}
          aria-label={takeTarget ? `Take ${takeTarget.displayName} onto slot ${slot.slot}` : `Take onto slot ${slot.slot}`}
          title={takeTarget ? undefined : 'Select an admitted participant first'}
          className={cn(actionButton, 'flex-1 bg-red-500/20 text-red-400 hover:bg-red-500/30')}
        >
          Take{takeTarget ? ` ${takeTarget.displayName}` : ''}
        </button>
        <button
          onClick={() => onAction({ kind: 'clear', slot: slot.slot })}
          disabled={busy || empty}
          aria-label={`Clear slot ${slot.slot}`}
          className={cn(actionButton, 'bg-jeket-black-elevated text-jeket-silver-muted hover:text-jeket-silver-bright')}
        >
          Clear
        </button>
      </div>
    </div>
  );
}

function AdmittedCard({
  p,
  selected,
  timeoutS,
  now,
  busy,
  onSelect,
  onAction,
}: {
  p: SlotParticipant;
  selected: boolean;
  timeoutS: number;
  now: number;
  busy: boolean;
  onSelect: () => void;
  onAction: (a: SlotAction) => void;
}) {
  return (
    <div
      data-testid={`participant-${p.participantId}`}
      className={cn(
        'bg-jeket-black-surface border rounded-lg p-3 flex items-center gap-3',
        selected ? 'border-jeket-gold ring-1 ring-jeket-gold/60' : 'border-jeket-healthy/30',
      )}
    >
      <button
        onClick={onSelect}
        aria-pressed={selected}
        aria-label={`Select ${p.displayName}`}
        className="flex-1 flex items-center gap-3 text-left"
      >
        <span className="w-8 h-8 rounded-full bg-jeket-black-elevated flex items-center justify-center text-xs text-jeket-silver-muted font-semibold shrink-0">
          {p.displayName.charAt(0).toUpperCase()}
        </span>
        <span className="min-w-0 space-y-0.5">
          <span className="block text-sm font-medium text-jeket-silver-bright truncate">{p.displayName}</span>
          <span className="flex items-center gap-3 text-[11px]">
            <Presence p={p} timeoutS={timeoutS} now={now} />
            <MediaState p={p} />
          </span>
        </span>
      </button>
      <span
        className={cn(
          'px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0',
          p.slot !== null ? 'bg-red-500/20 text-red-400' : 'bg-jeket-healthy/20 text-jeket-healthy',
        )}
      >
        {p.slot !== null ? `Slot ${p.slot}` : 'Waiting'}
      </span>
      <button
        onClick={() => onAction({ kind: 'remove', participant: p })}
        disabled={busy}
        aria-label={`Remove ${p.displayName}`}
        title="Remove from the event"
        className="p-1.5 rounded-lg bg-jeket-black-elevated text-jeket-silver-muted hover:text-jeket-error transition-colors disabled:opacity-40"
      >
        <UserX size={12} />
      </button>
    </div>
  );
}

function RequestedCard({ p, busy, onAction }: { p: SlotParticipant; busy: boolean; onAction: (a: SlotAction) => void }) {
  return (
    <div
      data-testid={`participant-${p.participantId}`}
      className="bg-jeket-black-surface border border-yellow-500/30 rounded-lg p-3 flex items-center gap-3"
    >
      <span className="flex-1 text-sm font-medium text-jeket-silver-bright truncate">{p.displayName}</span>
      <button
        onClick={() => onAction({ kind: 'approve', participant: p })}
        disabled={busy}
        aria-label={`Approve ${p.displayName}`}
        className={cn(actionButton, 'bg-jeket-healthy/20 text-jeket-healthy hover:bg-jeket-healthy/30')}
      >
        <UserCheck size={12} /> Approve
      </button>
      <button
        onClick={() => onAction({ kind: 'reject', participant: p })}
        disabled={busy}
        aria-label={`Reject ${p.displayName}`}
        className={cn(actionButton, 'bg-jeket-error/20 text-jeket-error hover:bg-jeket-error/30')}
      >
        <UserX size={12} /> Reject
      </button>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jeket-gold" />
    </div>
  );
}

function LoadError({ error }: { error: unknown }) {
  return (
    <p role="alert" className="text-sm text-jeket-error py-6">
      Couldn't load the guests: {parseApiError(error).message}
    </p>
  );
}

function NoParticipants() {
  return (
    <div className="text-center py-6">
      <Users size={48} className="mx-auto text-jeket-silver-muted/30 mb-3" />
      <p className="text-jeket-silver-muted">No participants yet</p>
      <p className="text-xs text-jeket-silver-muted/70 mt-1">Guests ask to join from the event's /join link</p>
    </div>
  );
}

/**
 * Below the moderator persona: who is on which slot and who is waiting, read
 * from the participant list (rows carry `slot`). The slot routes are the
 * moderator's, so N, empty slots and presence are not shown here.
 */
function GuestRoster({ eventId }: { eventId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['participants', eventId],
    queryFn: () => participantsApi.list(eventId),
    refetchInterval: POLL_MS,
  });
  if (isLoading) return <Spinner />;
  if (error || !data) return <LoadError error={error} />;

  const admitted = data.filter((p: EventParticipant) => p.status === 'approved');
  const onSlots = admitted.filter((p) => p.slot !== null).sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0));
  const waiting = admitted.filter((p) => p.slot === null);
  const requested = data.filter((p: EventParticipant) => p.status === 'requested');
  if (admitted.length === 0 && requested.length === 0) return <NoParticipants />;

  return (
    <div className="space-y-6" data-testid="guest-roster">
      <section aria-labelledby="roster-live-heading" className="space-y-2">
        <h3 id="roster-live-heading" className="text-sm font-medium text-red-400">
          On air ({onSlots.length})
        </h3>
        {onSlots.length === 0 ? (
          <p className="text-sm text-jeket-silver-muted">Nobody is on a slot.</p>
        ) : (
          <ul className="space-y-1">
            {onSlots.map((p) => (
              <li key={p.id} className="text-sm text-jeket-silver-bright">
                <span className="text-jeket-silver-muted">Slot {p.slot}:</span> {p.display_name}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section aria-labelledby="roster-waiting-heading" className="space-y-2">
        <h3 id="roster-waiting-heading" className="text-sm font-medium text-jeket-healthy">
          Admitted, waiting ({waiting.length})
        </h3>
        {waiting.length > 0 && (
          <ul className="space-y-1">
            {waiting.map((p) => (
              <li key={p.id} className="text-sm text-jeket-silver-bright">
                {p.display_name}
              </li>
            ))}
          </ul>
        )}
      </section>
      {requested.length > 0 && <p className="text-sm text-yellow-400">{requested.length} asked to join</p>}
      <p className="text-[11px] text-jeket-silver-muted">Running the guests takes the Event Operator role.</p>
    </div>
  );
}

export function ParticipantSlotsPanel({ eventId }: { eventId: string }) {
  const canModerate = canActAs(useUserRoles(), PARTICIPANT_MODERATOR_ROLE);
  return canModerate ? <ModeratorPanel eventId={eventId} /> : <GuestRoster eventId={eventId} />;
}

function ModeratorPanel({ eventId }: { eventId: string }) {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const queryKey = ['participant-slots', eventId];
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => participantSlotsApi.get(eventId),
    refetchInterval: POLL_MS,
  });

  const action = useMutation({
    mutationFn: (a: SlotAction) => perform(eventId, a),
    onSuccess: async (result, a) => {
      setRefusal(null);
      if (a.kind === 'take') setSelectedId(null);
      if (isSlotsView(result)) {
        // The slot writes answer with the whole view: show it at once. A poll
        // already in flight would land older state on top, so cancel it first.
        await queryClient.cancelQueries({ queryKey });
        queryClient.setQueryData(queryKey, result);
      } else {
        await queryClient.invalidateQueries({ queryKey });
      }
    },
    onError: async (err, a) => {
      setRefusal(refusalMessage(a, err));
      // A 409 may mean N changed under us, or the participant left: re-read.
      await queryClient.invalidateQueries({ queryKey });
    },
  });

  const participants = data?.participants ?? [];
  const admitted = participants.filter((p) => p.status === 'approved');
  const requested = participants.filter((p) => p.status === 'requested');
  const now = useNow(1_000, admitted.some((p) => !p.connected));

  if (isLoading) return <Spinner />;
  if (error || !data) return <LoadError error={error} />;

  const byId = new Map(participants.map((p) => [p.participantId, p]));
  const selected = (selectedId && admitted.find((p) => p.participantId === selectedId)) || null;
  const busy = action.isPending;
  const slots = [...data.slots].sort((a, b) => a.slot - b.slot);

  return (
    <div className="space-y-6">
      <section aria-labelledby="slots-heading" className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h3 id="slots-heading" className="text-sm font-medium text-red-400">
            Slots ({data.slotCount})
          </h3>
          <button
            onClick={() => action.mutate({ kind: 'fill' })}
            disabled={busy || data.slotCount === 0}
            aria-label="Fill all slots"
            title="Put admitted, connected participants onto free slots, in admission order"
            className={cn(actionButton, 'bg-jeket-gold/20 text-jeket-gold hover:bg-jeket-gold/30')}
          >
            <Users size={12} /> Fill all
          </button>
        </div>
        {data.slotCount === 0 ? (
          <p className="text-sm text-jeket-silver-muted" role="status">
            No site has declared slots for this event yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {slots.map((s) => (
              <SlotCard
                key={s.slot}
                slot={s}
                occupant={s.participantId ? (byId.get(s.participantId) ?? null) : null}
                selected={selected}
                timeoutS={data.presenceTimeoutS}
                now={now}
                busy={busy}
                onAction={(a) => action.mutate(a)}
              />
            ))}
          </div>
        )}
        {refusal && (
          <p role="alert" className="text-xs text-jeket-error">
            {refusal}
          </p>
        )}
      </section>

      <section aria-labelledby="admitted-heading" className="space-y-3">
        <div className="flex items-baseline justify-between gap-3">
          <h3 id="admitted-heading" className="text-sm font-medium text-jeket-healthy">
            Admitted ({admitted.length})
          </h3>
          <p className="text-[11px] text-jeket-silver-muted">
            {admitted.length > 0 ? 'Select a participant, then Take them onto a slot. ' : ''}A disconnected
            participant keeps their place for {data.presenceTimeoutS} s.
          </p>
        </div>
        {admitted.length === 0 ? (
          <p className="text-sm text-jeket-silver-muted">Nobody admitted yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {admitted.map((p) => (
              <AdmittedCard
                key={p.participantId}
                p={p}
                selected={selected?.participantId === p.participantId}
                timeoutS={data.presenceTimeoutS}
                now={now}
                busy={busy}
                onSelect={() => setSelectedId((cur) => (cur === p.participantId ? null : p.participantId))}
                onAction={(a) => action.mutate(a)}
              />
            ))}
          </div>
        )}
      </section>

      {requested.length > 0 && (
        <section aria-labelledby="requested-heading" className="space-y-3">
          <h3 id="requested-heading" className="text-sm font-medium text-yellow-400">
            Requested ({requested.length})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {requested.map((p) => (
              <RequestedCard key={p.participantId} p={p} busy={busy} onAction={(a) => action.mutate(a)} />
            ))}
          </div>
        </section>
      )}

      {participants.length === 0 && <NoParticipants />}
    </div>
  );
}

export default ParticipantSlotsPanel;
